<script lang="ts" setup>
/* eslint-disable */
import {useLoadingDataRefNull, extractError} from '@/ts/composables/pure/use-loading-data-ref';
import {useApi} from '@/ts/composables/stateful/use-api';
import {RouterLink, useRoute, useRouter} from 'vue-router';
import {computed, onMounted, ref, shallowRef, watch} from 'vue';
import LazyImage from '@/vue/atoms/lazy-image.vue';
import CompletedProgress from '@/vue/atoms/completed-progress.vue';
import {ReccoChallengeSessionStateDTO, ReccoChallengeStateDTO} from '@/ts/types/dto/recco/challenge';
import {AccountPath, MainPath, ContentPath} from '@/ts/router/pages-path';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import LoadingError from '@/vue/templates/loading-error.vue';
import BadgeZeppelin from '@/vue/atoms/badge-zeppelin.vue';
import BackLink from '@/vue/molecules/back-link.vue';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import {QScrollArea, useQuasar} from 'quasar';
import {useDragToScroll} from '@/ts/composables/pure/use-drag-to-scroll';
import {useI18n} from 'vue-i18n';
import BadgeEarnedModal from '@/vue/molecules/badge-earned-modal.vue';
import ChallengeExpiredModal from '@/vue/molecules/challenge-expired-modal.vue';
import {sessionStorage} from '@/ts/instances/session-storage';
import {isChallengeExpiredPendingAck} from '@/ts/utils/pure-functions';
import {useSnackbarStore} from '@/ts/store/snackbar-store';

const api = useApi();
const route = useRoute();
const router = useRouter();
const {t: $t} = useI18n();
const $q = useQuasar();
const snackbarStore = useSnackbarStore();

const [challenge, loading, error] = useLoadingDataRefNull(
    async() => api.content.getChallenge(Number(route.params.id)),
);

// Badge earned modal state
const showBadgeEarnedModal = ref(false);
const earnedBadge = shallowRef(sessionStorage.badgeEarned);

// Challenge expired modal state
const showExpiredModal = ref(false);

// Check sessionStorage for earned badge / expired challenge on mount
onMounted(() => {
  if (earnedBadge.value) {
    showBadgeEarnedModal.value = true;
    // Clear the storage so modal doesn't show again on refresh
    sessionStorage.badgeEarned = null;
  }
  // The session page detected the expiry and routed the user here carrying this flag.
  if (sessionStorage.challengeExpired) {
    sessionStorage.challengeExpired = false;
    showExpiredModal.value = true;
  }
});

// The user sat on the landing while the challenge expired underneath them: the GET surfaces it once.
watch(challenge, (value) => {
  if (isChallengeExpiredPendingAck(value)) {
    showExpiredModal.value = true;
  }
});

function onViewBadges(): void {
  void router.push(AccountPath.badges);
}

const challengeState = computed((): ReccoChallengeStateDTO => {
  // state NOT_JOINED is delivered as null by the BE.
  return challenge.value?.state ?? ReccoChallengeStateDTO.NOT_JOINED;
});

const stateLangToken = computed((): string => {
  let langToken = "recco_challenge_not_joined";
  switch (challengeState.value) {
    case ReccoChallengeStateDTO.COMPLETED:
      langToken = 'recco_challenge_completed';
      break;
    case ReccoChallengeStateDTO.FAILED:
      langToken = 'recco_challenge_failed';
      break;
    case ReccoChallengeStateDTO.WITHDREW:
      langToken = 'recco_challenge_withdrew';
      break;
    case ReccoChallengeStateDTO.IN_PROGRESS:
      langToken = 'recco_challenge_in_progress';
      break;
  }
  return langToken;
});

const displayActivateButton = computed((): boolean => {
  return challengeState.value === ReccoChallengeStateDTO.NOT_JOINED;
});

const displayActivateAgainButton = computed((): boolean => {
  return challengeState.value === ReccoChallengeStateDTO.WITHDREW ||
      challengeState.value === ReccoChallengeStateDTO.FAILED ||
      challengeState.value === ReccoChallengeStateDTO.COMPLETED;
});

const displayWithdrawButton = computed((): boolean => {
  return challengeState.value === ReccoChallengeStateDTO.IN_PROGRESS;
});

const isStateChangeInProgress = ref<boolean>(false);

const onClickActivate = async (): Promise<void> => {
  isStateChangeInProgress.value = true;
  try {
    await api.content.activateChallenge(challenge.value?.id!);
    challenge.value = await api.content.getChallenge(Number(route.params.id));
    confirmJoined();
  } catch (err) {
    $q.notify({type: 'negative', message: extractError(err, $t)});
  } finally {
    isStateChangeInProgress.value = false;
  }
};

/**
 * Joining otherwise changes nothing but the state pill (IPD-3328), so it is confirmed, with the way
 * into the session that has just opened. Read from the challenge as it came back, so the link only
 * names a session the page itself now offers; with none open there is nothing to lead to.
 */
function confirmJoined(): void {
  const firstOpen = challenge.value?.sessions.find(
    (session) => session.state === ReccoChallengeSessionStateDTO.IN_PROGRESS,
  );
  snackbarStore.show({
    message: $t('snackbar_challenge_joined'),
    ...(firstOpen && challenge.value ? {
      action: {
        label: $t('snackbar_challenge_action'),
        to: {name: ContentPath.challengeSession, params: {id: challenge.value.id, sessionId: firstOpen.id}},
      },
    } : {}),
    dismissible: true,
  });
}

const onClickWithdraw = async (): Promise<void> => {
  isStateChangeInProgress.value = true;
  try {
    await api.content.withdrawChallenge(challenge.value?.id!);
    challenge.value = await api.content.getChallenge(Number(route.params.id));
  } catch (err) {
    $q.notify({type: 'negative', message: extractError(err, $t)});
  } finally {
    isStateChangeInProgress.value = false;
  }
};

const unlockedSessions = computed((): Map<number, boolean> => {
  // Sessions are only navigable while the challenge is actively in progress (IPD-2784); an inactive
  // challenge (not-joined/withdrawn/failed/completed) renders them as non-clickable, matching the
  // challenge-session-page access guard so we never offer a link that just redirects to 404.
  const challengeActive = challengeState.value === ReccoChallengeStateDTO.IN_PROGRESS;
  return new Map(
      (challenge.value?.sessions ?? []).map((session => {
        return [
          session.id,
          challengeActive && (
            session.state === ReccoChallengeSessionStateDTO.IN_PROGRESS
            || session.state === ReccoChallengeSessionStateDTO.COMPLETED
          )
        ];
      }))
  );
});

const { scrollAreaRef, onMouseDown } = useDragToScroll();

</script>

<template>
  <loading-error type="page" id="challenge-page" :error="error" :loading="loading">
    <div v-if="challenge && !loading">

      <div class="header">
        <back-link :label="$t('recco_explore')" :to="MainPath.explore"/>
        <h1 class="text-display-1">{{ challenge.headline }}</h1>

        <badge-zeppelin class="state" :label="$t(stateLangToken)"/>

        <completed-progress
            v-if="challengeState === ReccoChallengeStateDTO.IN_PROGRESS"
            class="progress"
            :completed="challenge.pctCompletionProgress"
        />

        <div class="image-container">
          <div class="image-container-inner">
            <lazy-image class="image" :src="challenge.imageUrl" :width="800" :height="450"/>
          </div>
        </div>
      </div>

      <div class="intro-box">
        <div v-safe-html="challenge.descriptionHTML" class="intro-text"/>
        <div>
          <balm-btn v-if="displayActivateButton" @click="onClickActivate()" :label="$t('activate')" :loading="isStateChangeInProgress"/>
          <balm-btn v-if="displayActivateAgainButton" @click="onClickActivate()" :label="$t('activate')" :loading="isStateChangeInProgress"/>
          <balm-btn v-if="displayWithdrawButton" @click="onClickWithdraw()" :label="$t('withdraw')" :loading="isStateChangeInProgress"/>
        </div>
      </div>

      <div class="wrapper-for-sessions">
        <div class="scroll-area-container">
          <q-scroll-area ref="scrollAreaRef" class="scroll-area">
            <ul class="session-list" @mousedown="onMouseDown">
              <li v-for="session in challenge.sessions" :key="session.id" class="session-list-item">
                <component
                  :is="unlockedSessions.get(session.id) ? RouterLink : 'div'"
                  :to="unlockedSessions.get(session.id) ? {
                    name: ContentPath.challengeSession,
                    params: {
                      sessionId: session.id,
                      id: challenge.id,
                    }} : undefined"
                  :class="{
                    'session': true,
                    'session--locked': session.state === ReccoChallengeSessionStateDTO.LOCKED,
                    'session--completed': session.state === ReccoChallengeSessionStateDTO.COMPLETED,
                    'session--in-progress': session.state === ReccoChallengeSessionStateDTO.IN_PROGRESS,
                  }"
                >
                  <span class="text-body-sm-bold session-label">{{session.name}}</span>
                  <svg-illustration v-if="session.state === ReccoChallengeSessionStateDTO.IN_PROGRESS" name="arrow-narrow-right.svg" class="link-arrow"/>
                  <svg-illustration v-if="session.state === ReccoChallengeSessionStateDTO.COMPLETED" name="check.svg" class="check"/>

                </component>
              </li>
            </ul>
          </q-scroll-area>
        </div>
      </div>

    </div>

    <div v-if="error">
      Loading Error {{ route.params.id }}
    </div>

    <!-- Badge earned modal -->
    <badge-earned-modal
      v-if="earnedBadge"
      v-model="showBadgeEarnedModal"
      :badge="earnedBadge"
      @view-badges="onViewBadges"
    />

    <!-- Challenge expired modal -->
    <challenge-expired-modal v-model="showExpiredModal"/>
  </loading-error>
</template>

<style lang="scss" scoped>

.header {
  position: relative;
  background-color: var(--Secondary-secondary-color-5);
  padding: var(--Spacing-spacing-8) var(--Spacing-spacing-4) var(--Spacing-spacing-4) var(--Spacing-spacing-4);
  @include screen-md {
    min-height: 500px;
  }
  @include screen-lg {
    min-height: 600px;
  }
  @include screen-xl {
    min-height: 775px;
  }
}

.session {
  @include focus-visible;
  display: block;
  border-radius: var(--border-radius);
  padding: var(--Spacing-spacing-4);
  min-height: 200px;
  width: 200px;
  position: relative;
  background-color: var(--Neutral-UI-neutral-ui-20);

  @include screen-md {
    padding: var(--Spacing-spacing-2);
  }

  &--locked {
    background-color: var(--Neutral-UI-neutral-ui-20);
  }

  &--in-progress {
    background-color: var(--Secondary-secondary-color-1);
    &:hover,
    &:focus-visible {
      text-decoration: underline;
    }
  }

  &--completed {
    background-color: var(--Secondary-secondary-color-2);
    &:hover,
    &:focus-visible {
      text-decoration: underline;
    }
  }
}

.image-container {
  left: var(--Spacing-spacing-4);
  margin-top: var(--Spacing-spacing-10);
  @include screen-md {
    position: absolute;
    bottom: -100px;
    width: 45%;
    max-width: 770px;
    margin-top: 0;
  }
}

.image-container-inner {
  width: 100%;
  padding-bottom: 56%;
  background-color: var(--Primary-primary-color-5);
  overflow: hidden;
  position: relative;
  border-radius: var(--border-radius);
}

.image {
  position: absolute;
}

.intro-box {
  padding: var(--Spacing-spacing-4);
  margin-bottom: var(--Spacing-spacing-10);
  @include screen-md {
    margin-left: 50%;
  }
}

.intro-text {
  margin-bottom: var(--Spacing-spacing-4);
}

.state {
  margin-bottom: var(--Spacing-spacing-6);
}

.progress {
  max-width: 400px;
}

.session-list {
  list-style: none;
  // Padding to accommodate focus ring
  padding: 6px 4px 6px 12px;
  display: flex;
  gap: 8px;

  @include screen-md {
    gap: 12px;
  }

  @include screen-lg {
    gap: 16px;
  }
}

.session-list-item {
  padding: 0;
}

.link-arrow {
  position: absolute;
  bottom: var(--Spacing-spacing-4);
  right: var(--Spacing-spacing-4);
  @include screen-md {
    bottom: var(--Spacing-spacing-2);
    right: var(--Spacing-spacing-2);
  }
}

.check {
  position: absolute;
  top: var(--Spacing-spacing-4);
  right: var(--Spacing-spacing-4);
  @include screen-md {
    bottom: var(--Spacing-spacing-2);
    right: var(--Spacing-spacing-2);
  }
}

.session-label {
  margin: var(--Spacing-spacing-6) 0;
  display: block;
}

.scroll-area {
  height: 250px;
}

.scroll-area-container {
  margin: 0 calc(var(--Spacing-spacing-2) * -1);
  position: relative;

  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 15px;
    width: 5px;
    z-index: 1;
    background: linear-gradient(to right, var(--body-background-color) 0%,
        color-mix(in srgb, var(--body-background-color), transparent 100%) 100%);
  }

  &:after {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    bottom: 15px;
    width: 5px;
    z-index: 1;
    background: linear-gradient(to left, var(--body-background-color) 0%,
        color-mix(in srgb, var(--body-background-color), transparent 100%) 100%);
  }
}

.wrapper-for-sessions {
  margin: 0 var(--Spacing-spacing-4) var(--Spacing-spacing-10) var(--Spacing-spacing-4);
}

</style>